import { ImageBox } from '../components/BoxedImage';

/**
 * A clip-strip / mid-shelf decision emitted by the backend planogram-compliance repair.
 *
 * - `'L'` / `'R'`: a synthetic price tag was added on the left / right shelf edge (clip-strip).
 * - `'M'`: a synthetic price tag was added inside a mid-shelf merchandise gap (mid-shelf repair).
 * - `'B'`: the shelf was considered but no tag was added (baseline).
 */
export type ClipStripDecision = 'B' | 'L' | 'R' | 'M';

/** Map keyed by `"{mod}-{shelf}"` (e.g. `"9-3"`). */
export type ClipStripDecisions = Record<string, ClipStripDecision>;

/** A per-shelf oversized-price-tag decision. */
export type OversizedPtDecisions = Record<string, string>;

type PositionVector =
  | {
      mod: number;
      shelf: number;
    }
  | null
  | undefined;

type ClipStripBox = {
  shape?: [number, number, number, number] | null;
  rogPositionVector?: PositionVector;
  pogPositionVector?: PositionVector;
};

// A small nominal box size (in natural-image pixels) so the badge renderer's
// adaptive scaling has a sane dimension to work from. The badge itself is drawn
// relative to this anchor, not filling it.
const NOMINAL_BADGE_ANCHOR_SIZE = 90;

/**
 * Resolve the `"{mod}-{shelf}"` key for a compliance box, preferring the
 * realogram position vector and falling back to the planogram one.
 *
 * Returns `null` when neither vector carries a usable mod/shelf pair.
 */
export function getBoxShelfKey(box: ClipStripBox): string | null {
  const vector = box.rogPositionVector ?? box.pogPositionVector;
  if (!vector || vector.mod === undefined || vector.shelf === undefined) {
    return null;
  }
  return `${vector.mod}-${vector.shelf}`;
}

/** The `"{mod}"` prefix of a `"{mod}-{shelf}"` key. */
function getModId(shelfKey: string): string {
  return shelfKey.split('-')[0];
}

type ModExtent = { minX: number; maxX: number };

/**
 * Horizontal extent (in natural-image pixels) of every real box belonging to a
 * mod, so all of a mod's badges can share one X column regardless of which
 * shelf they sit on. Zero-area placeholder boxes — planogram-only boxes
 * normalized to a `[0, 0, 0, 0]` shape upstream — are skipped so they don't
 * collapse the extent to the image origin.
 */
function computeModExtents(boxes: ClipStripBox[]): Map<string, ModExtent> {
  const extents = new Map<string, ModExtent>();

  for (const box of boxes) {
    if (!box.shape || box.shape.length !== 4) {
      continue;
    }

    const [x, , width, height] = box.shape;
    if (width <= 0 || height <= 0) {
      continue;
    }

    const shelfKey = getBoxShelfKey(box);
    if (!shelfKey) {
      continue;
    }

    const modId = getModId(shelfKey);
    const current = extents.get(modId);
    if (current) {
      current.minX = Math.min(current.minX, x);
      current.maxX = Math.max(current.maxX, x + width);
    } else {
      extents.set(modId, { minX: x, maxX: x + width });
    }
  }

  return extents;
}

/**
 * Top Y (in natural-image pixels) of the shelf's real boxes, used to place the
 * badge vertically at its own shelf. Zero-area placeholder boxes are skipped.
 * Returns `null` when the shelf has no usable boxes.
 */
function computeShelfTopY(boxes: ClipStripBox[], shelfKey: string): number | null {
  let minY = Infinity;
  let matched = false;

  for (const box of boxes) {
    if (!box.shape || box.shape.length !== 4) {
      continue;
    }

    const [, y, width, height] = box.shape;
    if (width <= 0 || height <= 0) {
      continue;
    }

    if (getBoxShelfKey(box) !== shelfKey) {
      continue;
    }

    matched = true;
    minY = Math.min(minY, y);
  }

  return matched ? minY : null;
}

/** Which shelf corner a badge hugs. */
type BadgeSide = 'left' | 'right';

/**
 * Build one tiny, non-interactive anchor box per shelf present in `decisions`,
 * tagged with the given badge field so {@link ImageBox} can render it.
 *
 * Each anchor carries `badgeStackIndex`, letting the renderer stack multiple
 * badge kinds vertically on the same shelf corner without overlapping.
 *
 * Shelves with no matching boxes are skipped rather than throwing.
 */
function buildShelfBadgeBoxes(
  boxes: ClipStripBox[] | null | undefined,
  decisions: Record<string, string> | null | undefined,
  options: {
    field: 'clipStripBadge' | 'oversizedPtBadge';
    side: BadgeSide | ((decision: string) => BadgeSide);
    stackIndex: number;
  }
): ImageBox[] {
  if (!decisions || !Array.isArray(boxes) || boxes.length === 0) {
    return [];
  }

  const modExtents = computeModExtents(boxes);
  const badges: ImageBox[] = [];

  for (const [shelfKey, decision] of Object.entries(decisions)) {
    // X is shared across the whole mod so every badge in a mod lines up in one
    // column; Y stays per-shelf so each badge sits at its own shelf.
    const modExtent = modExtents.get(getModId(shelfKey));
    const topY = computeShelfTopY(boxes, shelfKey);
    if (!modExtent || topY === null) {
      continue;
    }

    const side = typeof options.side === 'function' ? options.side(decision) : options.side;
    const anchorX = side === 'right' ? modExtent.maxX - NOMINAL_BADGE_ANCHOR_SIZE : modExtent.minX;

    badges.push({
      shape: [anchorX, topY, NOMINAL_BADGE_ANCHOR_SIZE, NOMINAL_BADGE_ANCHOR_SIZE],
      color: 'transparent',
      strokeWidth: 0,
      opacity: 0,
      [options.field]: decision,
      badgeStackIndex: options.stackIndex,
    });
  }

  return badges;
}

/**
 * Build one clip-strip badge overlay per shelf present in `clipStripDecisions`,
 * anchored at a shelf corner:
 * - `'L'` -> top-left of the shelf span
 * - `'R'` -> top-right of the shelf span
 * - `'B'` -> top-left (neutral baseline)
 */
export function buildClipStripBadgeBoxes(
  boxes: ClipStripBox[] | null | undefined,
  clipStripDecisions: ClipStripDecisions | null | undefined
): ImageBox[] {
  return buildShelfBadgeBoxes(boxes, clipStripDecisions, {
    field: 'clipStripBadge',
    side: (decision) => (decision === 'R' ? 'right' : 'left'),
    stackIndex: 0,
  });
}

/**
 * Build one oversized-price-tag badge overlay per shelf present in
 * `oversizedPtDecisions`, anchored at the top-left of the shelf span and stacked
 * below any clip-strip badge on the same shelf so the two never overlap.
 */
export function buildOversizedPtBadgeBoxes(
  boxes: ClipStripBox[] | null | undefined,
  oversizedPtDecisions: OversizedPtDecisions | null | undefined
): ImageBox[] {
  return buildShelfBadgeBoxes(boxes, oversizedPtDecisions, {
    field: 'oversizedPtBadge',
    side: 'left',
    stackIndex: 1,
  });
}
